import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { collection, getDocs, query, where, orderBy, Timestamp, doc, deleteDoc } from 'firebase/firestore';
import { db } from '../../firebase';
import { useLocation } from 'react-router-dom';
import moment from 'moment';
import './LaporanPenggunaanAlat.css';
import { jsPDF } from "jspdf";
import autoTable from 'jspdf-autotable';
import { 
  Box, 
  Typography, 
  Button, 
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TablePagination,
  Autocomplete,
  TextField,
  CircularProgress
} from '@mui/material';

// Helper to format Firestore Timestamp to readable date and time string
const formatDateTime = (timestamp) => {
  if (timestamp instanceof Timestamp) {
    return timestamp.toDate().toLocaleString('id-ID', {
      year: 'numeric', month: 'short', day: 'numeric',
      hour: '2-digit', minute: '2-digit', hour12: false
    });
  }
  return 'N/A';
};

// Helper to get the start of a day for a date string 'YYYY-MM-DD'
const getStartOfDayTimestamp = (dateString) => {
  if (!dateString) return null;
  const date = new Date(dateString);
  date.setHours(0, 0, 0, 0);
  return Timestamp.fromDate(date);
};

// Helper to get the end of a day for a date string 'YYYY-MM-DD'
const getEndOfDayTimestamp = (dateString) => {
  if (!dateString) return null;
  const date = new Date(dateString);
  date.setHours(23, 59, 59, 999);
  return Timestamp.fromDate(date);
};

const LaporanPenggunaanAlat = () => {
  const [alatList, setAlatList] = useState([]);
  const [selectedAlat, setSelectedAlat] = useState(null);
  const [usageData, setUsageData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const location = useLocation();
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [filterStartDate, setFilterStartDate] = useState('');
  const [filterEndDate, setFilterEndDate] = useState('');

  // Fetch list of instruments and glassware
  const fetchAlatList = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const instrumenRef = collection(db, 'instrumen');
      const alatGelasRef = collection(db, 'alat_gelas');

      const [instrumenSnapshot, alatGelasSnapshot] = await Promise.all([
        getDocs(instrumenRef),
        getDocs(alatGelasRef)
      ]);

      const instrumenData = instrumenSnapshot.docs.map(doc => ({ id: doc.id, type: 'instrumen', ...doc.data() }));
      const alatGelasData = alatGelasSnapshot.docs.map(doc => ({ id: doc.id, type: 'alat_gelas', ...doc.data() }));

      const combinedList = [...instrumenData, ...alatGelasData].sort((a, b) => a.nama.localeCompare(b.nama));
      setAlatList(combinedList);

      if (location.state?.alatId && combinedList.length > 0) {
        const preselectedAlat = combinedList.find(item => item.id === location.state.alatId);
        if (preselectedAlat) {
          setTimeout(() => fetchUsageData(preselectedAlat), 0);
        } else {
          console.warn(`Preselected alat with ID ${location.state.alatId} not found in the fetched list.`);
        }
      }
    } catch (err) {
      console.error("Error fetching tools list:", err);
      setError('Gagal memuat daftar alat.');
    } finally {
      setLoading(false);
    }
  }, [location.state]);

  // Filtered list based on search term
  const filteredAlatList = useMemo(() => {
    if (!searchTerm) {
      return alatList;
    }
    return alatList.filter(item =>
      item.nama.toLowerCase().includes(searchTerm.toLowerCase())
    );
  }, [alatList, searchTerm]);

  // Fetch usage data for the selected alat
  const fetchUsageData = useCallback(async (alat = null) => {
    setLoading(true);
    setError('');
    setSelectedAlat(alat);
    setUsageData([]);
    try {
      const requestsRef = collection(db, 'permintaan_alat');
      let q;

      // Base query conditions
      const conditions = [
        where('status', 'in', ['admin_approved', 'returned']),
        orderBy('tanggalPinjam', 'desc')
      ];

      // Add alat filter if selected
      if (alat) {
        conditions.unshift(where('namaAlat', '==', alat.nama));
      }

      // Add date range filters if provided
      if (filterStartDate) {
        const startTimestamp = getStartOfDayTimestamp(filterStartDate);
        conditions.push(where('tanggalPinjam', '>=', startTimestamp));
      }
      if (filterEndDate) {
        const endTimestamp = getEndOfDayTimestamp(filterEndDate);
        conditions.push(where('tanggalPinjam', '<=', endTimestamp));
      }

      q = query(requestsRef, ...conditions);

      const querySnapshot = await getDocs(q);
      const usage = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));

      setUsageData(usage);
    } catch (err) {
      const errorMessage = alat 
        ? `Gagal memuat data penggunaan untuk ${alat.nama}.`
        : 'Gagal memuat data penggunaan alat.';
      console.error('Error fetching usage data:', err);
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [filterStartDate, filterEndDate]);

  // Function to delete a usage record
  const handleDeleteRecord = async (recordId) => {
    if (!window.confirm('Apakah Anda yakin ingin menghapus riwayat penggunaan ini?')) {
      return;
    }
    setError('');
    setLoading(true);
    try {
      const requestRef = doc(db, 'permintaan_alat', recordId);
      await deleteDoc(requestRef);
      if (selectedAlat) {
        fetchUsageData(selectedAlat);
      } else {
        setUsageData([]);
        setLoading(false);
      }
    } catch (err) {
      console.error("Error deleting record:", err);
      setError('Gagal menghapus riwayat penggunaan.');
      setLoading(false);
    }
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  useEffect(() => {
    fetchAlatList();
    // Load all usage data initially
    fetchUsageData();
  }, [fetchAlatList]);

  // Handle filter application
  const handleApplyFilter = () => {
    fetchUsageData(selectedAlat);
  };

  // Generate PDF report
  const handleGeneratePDF = () => {
    try {
      const doc = new jsPDF();
      
      // Title
      doc.setFontSize(16);
      doc.text('Laporan Penggunaan Alat', 14, 15);
      
      // Date Range
      doc.setFontSize(10);
      const dateRange = filterStartDate && filterEndDate
        ? `Periode: ${new Date(filterStartDate).toLocaleDateString('id-ID')} - ${new Date(filterEndDate).toLocaleDateString('id-ID')}`
        : 'Semua Periode';
      doc.text(dateRange, 14, 25);

      // Group equipment usage by name and calculate totals
      const equipmentUsage = {};
      usageData.forEach(req => {
        const key = req.namaAlat;
        if (!equipmentUsage[key]) {
          equipmentUsage[key] = {
            total: 0,
            count: 0
          };
        }
        equipmentUsage[key].total += req.jumlah || 1;
        equipmentUsage[key].count += 1;
      });

      // Summary Table
      doc.setFontSize(12);
      doc.text('Ringkasan Penggunaan Alat', 14, 35);

      const summaryTableBody = Object.entries(equipmentUsage).map(([alat, data]) => [
        alat,
        `${data.total} kali`
      ]);

      autoTable(doc, {
        startY: 40,
        head: [['Nama Alat', 'Total Penggunaan']],
        body: summaryTableBody,
        theme: 'grid',
        headStyles: { fillColor: [22, 160, 133] }
      });

      // Detailed Table
      doc.setFontSize(12);
      doc.text('Detail Penggunaan Alat', 14, doc.lastAutoTable.finalY + 15);

      const detailedTableBody = usageData.map(req => [
        req.namaAlat,
        req.namaUser,
        req.userNim,
        req.jumlah || 1,
        formatDateTime(req.tanggalPinjam),
        formatDateTime(req.tanggalKembali),
        req.status === 'admin_approved' ? 'Disetujui' : 'Dikembalikan'
      ]);

      autoTable(doc, {
        startY: doc.lastAutoTable.finalY + 20,
        head: [['Nama Alat', 'Peminta', 'NIM', 'Jumlah', 'Tanggal Pinjam', 'Tanggal Kembali', 'Status']],
        body: detailedTableBody,
        theme: 'grid',
        headStyles: { fillColor: [22, 160, 133] },
        styles: { fontSize: 8 },
        columnStyles: {
          0: { cellWidth: 35 },
          1: { cellWidth: 35 },
          2: { cellWidth: 25 },
          3: { cellWidth: 15 },
          4: { cellWidth: 25 },
          5: { cellWidth: 25 },
          6: { cellWidth: 20 }
        }
      });

      // Save the PDF
      doc.save(`Laporan_Penggunaan_Alat_${Date.now()}.pdf`);

    } catch (err) {
      console.error("Error generating PDF:", err);
      setError('Gagal membuat file PDF.');
    }
  };

  return (
    <div className="laporan-alat-container">
      <h2>Laporan Penggunaan Alat</h2>

      {error && <div className="error-message">{error}</div>}

      <Box sx={{ mb: 3 }}>
        <Typography variant="h6" gutterBottom>Filter Laporan</Typography>
        
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'flex-start' }}>
          {/* Equipment Search */}
          <Box sx={{ minWidth: 300 }}>
            <Autocomplete
              options={alatList}
              getOptionLabel={(option) => 
                `${option.nama} (${option.type === 'instrumen' ? 'Instrumen' : 'Alat Gelas'})`
              }
              onChange={(event, newValue) => {
                setSelectedAlat(newValue);
                fetchUsageData(newValue);
              }}
              value={selectedAlat}
          loading={loading && !selectedAlat}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Cari alat..."
              variant="outlined"
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <React.Fragment>
                    {loading && !selectedAlat && (
                      <CircularProgress color="inherit" size={20} />
                    )}
                    {params.InputProps.endAdornment}
                  </React.Fragment>
                ),
              }}
            />
          )}
          renderOption={(props, option) => (
            <Box component="li" {...props}>
              <Box>
                <Typography variant="body1">
                  {option.nama}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {option.type === 'instrumen' ? 'Instrumen' : 'Alat Gelas'}
                  {option.keterangan && ` - ${option.keterangan}`}
                </Typography>
              </Box>
            </Box>
          )}
          disabled={loading}
        />
      </Box>

          {/* Date Filters */}
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
            <TextField
              type="date"
              label="Dari Tanggal"
              value={filterStartDate}
              onChange={(e) => setFilterStartDate(e.target.value)}
              size="small"
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              type="date"
              label="Sampai Tanggal"
              value={filterEndDate}
              onChange={(e) => setFilterEndDate(e.target.value)}
              size="small"
              InputLabelProps={{ shrink: true }}
            />
            <Button 
              variant="contained" 
              onClick={handleApplyFilter} 
              disabled={loading}
              size="small"
            >
              Terapkan Filter
            </Button>
            <Button 
              variant="contained"
              onClick={handleGeneratePDF}
              disabled={loading || usageData.length === 0}
              size="small"
              color="primary"
            >
              Generate PDF
            </Button>
          </Box>
        </Box>
      </Box>

      <div className="usage-report-container">
        <h3>Laporan Penggunaan: {selectedAlat ? selectedAlat.nama : 'Semua Alat'}</h3>
        {loading ? (
          <div className="loading-message">Memuat data...</div>
        ) : (
          <TableContainer component={Paper}>
              <Table sx={{ minWidth: 650 }} aria-label="laporan penggunaan alat">
                <TableHead>
                  <TableRow>
                    <TableCell>Nama Alat</TableCell>
                    <TableCell>Pengguna</TableCell>
                    <TableCell>NIM</TableCell>
                    <TableCell>Program Studi</TableCell>
                    <TableCell>Jumlah</TableCell>
                    <TableCell>Tanggal Pinjam</TableCell>
                    <TableCell>Tanggal Kembali</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Dikembalikan Aktual</TableCell>
                    <TableCell>Aksi</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(rowsPerPage > 0
                    ? usageData.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                    : usageData
                  ).map((record) => (
                    <TableRow key={record.id}>
                      <TableCell>{record.namaAlat || 'N/A'}</TableCell>
                      <TableCell>{record.namaUser || 'N/A'}</TableCell>
                      <TableCell>{record.userNim || 'N/A'}</TableCell>
                      <TableCell>{record.userProgramStudi || 'N/A'}</TableCell>
                      <TableCell>{record.jumlah || 1}</TableCell>
                      <TableCell>{formatDateTime(record.tanggalPinjam)}</TableCell>
                      <TableCell>{formatDateTime(record.tanggalKembali)}</TableCell>
                      <TableCell>
                        {record.status === 'admin_approved' ? 'Disetujui' : 
                         record.status === 'returned' ? 'Dikembalikan' : 
                         record.status}
                      </TableCell>
                      <TableCell>
                        {record.tanggalDikembalikanActual ? 
                          formatDateTime(record.tanggalDikembalikanActual) : 
                          'Belum dikembalikan'}
                      </TableCell>
                      <TableCell>
                        <Button
                          variant="contained"
                          color="error"
                          size="small"
                          onClick={() => handleDeleteRecord(record.id)}
                          disabled={loading}
                        >
                          Hapus
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                  {usageData.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={10} align="center">
                        Tidak ada data penggunaan untuk alat ini.
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
              <TablePagination
                rowsPerPageOptions={[5, 10, 25]}
                component="div"
                count={usageData.length}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                labelRowsPerPage="Baris per halaman"
                labelDisplayedRows={({ from, to, count }) => 
                  `${from}-${to} dari ${count !== -1 ? count : `lebih dari ${to}`}`
                }
              />
            </TableContainer>
          )}
      </div>
    </div>
  );
};

export default LaporanPenggunaanAlat;
